/**
 * Résolution de l'URL de base de l'API AU RUNTIME (et non figée au build).
 *
 * Ordre de priorité :
 *   1. `window.__APP_CONFIG__.apiBaseUrl` — défini par `public/config.js`,
 *      fichier NON bundlé (nom stable, sans hash) donc écrasable par
 *      environnement via un volume Docker monté sur
 *      `/usr/share/nginx/html/config.js`.
 *   2. `process.env.VUE_APP_API` — valeur compilée (fallback ; sert au
 *      `yarn serve` de dev et si aucun config.js n'est servi).
 *   3. `http://localhost:8000` — dernier recours (dev local sans config).
 *
 * Objectif : UNE seule image pour tous les environnements. On monte un
 * `config.js` différent par env (beta → api-beta, prod → api.jahagal, …)
 * sans rebuild. Voir `deploy/README.md`.
 *
 * Drop-in 1:1 de `process.env.VUE_APP_API` : retourne la même valeur
 * lorsqu'aucune config runtime n'est présente.
 */
export function getApiBaseUrl(): string {
	const runtime = (window as any)?.__APP_CONFIG__?.apiBaseUrl;
	if (typeof runtime === "string" && runtime.trim() !== "") {
		return runtime.trim();
	}
	return process.env.VUE_APP_API || "http://localhost:8000";
}

/**
 * Paramètres Reverb (WebSocket) résolus AU RUNTIME, même principe et même
 * ordre de priorité que `getApiBaseUrl`.
 *
 * Ils étaient lus depuis `process.env.VUE_APP_REVERB_*`, donc figés au build :
 * il fallait une image par environnement, alors que `apiBaseUrl` était déjà
 * runtime. Une seule valeur cuite suffit à annuler le bénéfice de l'image
 * unique — d'où ce complément.
 */
export function getReverbConfig(): {
	host: string;
	port: number;
	key: string;
	scheme: string;
} {
	const c = (window as any)?.__APP_CONFIG__ ?? {};
	const lire = (v: unknown, secours: string): string =>
		typeof v === "string" && v.trim() !== "" ? v.trim() : secours;

	return {
		host: lire(c.reverbHost, process.env.VUE_APP_REVERB_HOST || "localhost"),
		port: Number(lire(c.reverbPort, process.env.VUE_APP_REVERB_PORT || "8082")),
		key: lire(c.reverbKey, process.env.VUE_APP_REVERB_KEY || "jahagal-key-dev"),
		scheme: lire(c.reverbScheme, process.env.VUE_APP_REVERB_SCHEME || "http"),
	};
}

/**
 * Duree de vie du cookie de session, en SECONDES.
 *
 * Etait lue sur `process.env.VUE_APP_COOKIE_MAXAGE`, une valeur figee au build
 * et fournie par le `.env` que l'ancien pipeline copiait avant de compiler. Le
 * pipeline actuel construit UNE image pour tous les paliers et ne fournit plus
 * ce fichier : la variable valait `undefined`, `parseInt(undefined)` rendait
 * NaN, et js-cookie refusait le cookie :
 *
 *     TypeError: option maxAge is invalid
 *
 * La connexion echouait entierement — le jeton n'etait jamais enregistre.
 *
 * 7200 s (2 h) est la valeur que portait le .env de reference.
 */
export function getCookieMaxAge(): number {
	const c = (window as any)?.__APP_CONFIG__ ?? {};
	const n = Number(c.cookieMaxAge ?? process.env.VUE_APP_COOKIE_MAXAGE);
	return Number.isFinite(n) && n > 0 ? n : 7200;
}

/**
 * Combien de temps AVANT l'expiration le jeton est rafraichi, en MILLISECONDES.
 *
 * Meme cause que ci-dessus, defaut plus discret : `exp * 1000 - undefined`
 * donne NaN, `setTimeout` traite NaN comme 0, et le rafraichissement partait
 * en boucle immediate au lieu d'attendre.
 *
 * 300000 ms (5 min) est la valeur du .env de reference.
 */
export function getRefreshTimeReduction(): number {
	const c = (window as any)?.__APP_CONFIG__ ?? {};
	const n = Number(c.refreshTimeReduction ?? process.env.VUE_APP_REFRESH_TIME_REDUCTION);
	return Number.isFinite(n) && n > 0 ? n : 300000;
}
